import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AdminVendor,
  App,
  AppChange,
  AppWaveTags,
  CreateVendorAsAdminInput,
  UpdateAppInput,
  UpdateVendorAsAdminInput,
  User,
  Version,
} from '@keboola/api-client/developerPortal/types';

import { api } from '../../api';
import { fetchAllPages } from '../../paging';

import { COMPONENT_CHANGES_DAYS } from './constants';
import { samePins } from './waves';

export const adminKeys = {
  all: ['admin'] as const,
  apps: ['admin', 'apps'] as const,
  app: (appId: string) => ['admin', 'apps', appId] as const,
  appVersions: (appId: string) => ['admin', 'apps', appId, 'versions'] as const,
  appWave: (appId: string, wave: number) => ['admin', 'apps', appId, 'wave', wave] as const,
  changes: (appId: string) => ['admin', 'changes', appId] as const,
  users: ['admin', 'users'] as const,
  vendors: ['admin', 'vendors'] as const,
} as const;

/**
 * Every component across all vendors.
 *
 * - Paged through: `limit` caps at 1000 and all filters are client-side, so a truncated list skews them.
 */
export const useAdminApps = () =>
  useQuery({
    queryKey: adminKeys.apps,
    queryFn: ({ signal }): Promise<App[]> =>
      fetchAllPages((range) => api.admin.getApps(range, signal), 'components across all vendors'),
  });

export const useAdminApp = (appId: string) =>
  useQuery({
    queryKey: adminKeys.app(appId),
    queryFn: ({ signal }) => api.admin.getApp(appId, signal),
  });

/** The component as a stack on each of `waves` gets it: `rolloutWave` names the pin apps-api applies. */
export const useAdminAppForWaves = (appId: string, waves: readonly number[]) =>
  useQueries({
    queries: waves.map((wave) => ({
      queryKey: adminKeys.appWave(appId, wave),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.admin.getAppForWave(appId, wave, signal),
    })),
  });

/** Every version of a component, `waveTags` included. */
export const useAdminAppVersions = (appId: string) =>
  useQuery({
    queryKey: adminKeys.appVersions(appId),
    queryFn: async ({ signal }): Promise<Version[]> => {
      const rows = await fetchAllPages(
        (range) => api.admin.getAppVersions(appId, range, signal),
        "this component's versions",
      );
      // The admin list is typed `App`, whose `createdOn` may be `null`; `Version` has no `null`.
      return rows.map((row): Version => ({ ...row, createdOn: row.createdOn ?? undefined }));
    },
  });

/**
 * Replaces every wave pin of a component; `null` clears them.
 *
 * - Refuses when the live pins are no longer `from`, so a releaser move is never overwritten unseen.
 * - The check is client-side: apps-api's `If-Match` is not an allowed CORS header.
 */
export const useSetWavePins = (appId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ from, to }: { from: unknown; to: AppWaveTags | null }) => {
      const live = await api.admin.getApp(appId);
      if (!samePins(live.waveTags, from)) {
        throw new Error(
          'The pins changed after this dialog opened, for example by the wave releaser. Close it and open it again.',
        );
      }
      return api.admin.updateApp(appId, { waveTags: to });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apps, exact: false });
    },
  });
};

/**
 * A component's change log.
 *
 * - `since` is required — apps-api returns nothing without it.
 * - The date is built once per query key, not per render, so the cache key stays stable.
 */
export const useAppChanges = (appId: string) =>
  useQuery({
    queryKey: adminKeys.changes(appId),
    queryFn: ({ signal }): Promise<AppChange[]> => {
      const since = new Date();
      since.setDate(since.getDate() - COMPONENT_CHANGES_DAYS);
      return api.admin.getChanges({ appId, since: since.toISOString().slice(0, 10) }, signal);
    },
  });

/**
 * Updates a component via `/admin/apps/{id}`, not the vendor route — so the edit form takes its mutation as a prop.
 */
export const useAdminUpdateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, input }: { appId: string; input: UpdateAppInput }) =>
      api.admin.updateApp(appId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apps, exact: false });
    },
  });
};

export const usePublishApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appId: string) => api.admin.publishApp(appId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apps, exact: false });
    },
  });
};

export const useRejectApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, reason }: { appId: string; reason: string }) =>
      api.admin.rejectApp(appId, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apps, exact: false });
    },
  });
};

/**
 * Deletes a component through the vendor-scoped route — there is no `DELETE /admin/apps/{id}`.
 */
export const useAdminDeleteApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vendorId, appId }: { vendorId: string; appId: string }) =>
      api.apps.deleteApp(vendorId, appId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apps, exact: false });
    },
  });
};

/**
 * Every developer, paged through and filtered client-side.
 *
 * - The vendor filter has no server equivalent, so the server-side `filter` is unused.
 * - A truncated list would report "no match" for an account that exists.
 */
export const useAdminUsers = () =>
  useQuery({
    queryKey: adminKeys.users,
    queryFn: ({ signal }): Promise<User[]> =>
      fetchAllPages((range) => api.admin.getUsers(range, signal), 'developers'),
  });

/**
 * Every vendor, paged through: it feeds `knownVendorIds`, so a truncated list reports false orphans.
 */
export const useAdminVendors = () =>
  useQuery({
    queryKey: adminKeys.vendors,
    // `AdminVendor`: only it types `isPublic`, `isApproved` and `isTrusted`; `Vendor` leaves them `unknown`.
    queryFn: ({ signal }): Promise<AdminVendor[]> =>
      fetchAllPages((range) => api.admin.getVendors(range, signal), 'vendors'),
  });

/** Adds or removes a vendor membership. */
export const useSetUserVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      action,
      username,
      vendorId,
    }: {
      action: 'add' | 'remove';
      username: string;
      vendorId: string;
    }) =>
      action === 'add'
        ? api.admin.addUserToVendor(username, vendorId)
        : api.admin.removeUserFromVendor(username, vendorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
};

export const useDisableMfa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => api.admin.resetUserMfa(email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
};

export const useMakeUserAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => api.admin.grantUserAdmin(email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
};

export const useCreateVendorAsAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateVendorAsAdminInput) => api.admin.createVendor(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.vendors });
    },
  });
};

/**
 * Edits a vendor via the admin route — the vendor-scoped one answers 403 to a non-member.
 */
export const useAdminUpdateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vendorId, input }: { vendorId: string; input: UpdateVendorAsAdminInput }) =>
      api.admin.updateVendor(vendorId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.vendors });
    },
  });
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vendorId: string) => api.admin.deleteVendor(vendorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.vendors });
    },
  });
};

/**
 * Approves a pending vendor, renaming it in the same call.
 *
 * - The rename is required: temporary `_v`-prefixed ids are hidden from users' selectable vendors.
 */
export const useApproveVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vendorId, newId }: { vendorId: string; newId: string }) =>
      api.admin.approveVendor(vendorId, { newId }),
    onSuccess: () => {
      // The rename changes user memberships too.
      void queryClient.invalidateQueries({ queryKey: adminKeys.all, exact: false });
    },
  });
};
